import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { logger } from '../utils/logger'
import {
  ArrowLeft,
  Plus,
  Edit, 
  Trash2, 
  Bot, 
  UserCheck, 
  AlertCircle,
  RefreshCw,
  Search,
  CheckCircle,
  XCircle,
  DollarSign
} from 'lucide-react'
import { subscriptionPlanService, SubscriptionPlan, CreatePlanRequest } from '../services/subscriptionPlanService'
import toast from 'react-hot-toast'

// Função utilitária para formatar preço
const formatPrice = (price: any): string => {
  if (!price) return '0.00'
  const numPrice = typeof price === 'string' ? parseFloat(price) : price
  return isNaN(numPrice) ? '0.00' : numPrice.toFixed(2)
}

const SubscriptionPlans: React.FC = () => {
  const navigate = useNavigate()
  const [plans, setPlans] = useState<SubscriptionPlan[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  })
  const [searchTerm, setSearchTerm] = useState('')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null)
  const [formData, setFormData] = useState<CreatePlanRequest>({
    plan_type: 'basic',
    name: '',
    description: '',
    max_bots: 1,
    max_attendants: 3,
    features: {},
    price: 0,
    is_active: true
  })

  const planTypeConfig = {
    basic: {
      name: 'Básico',
      color: 'bg-gray-500',
      icon: '🥉'
    },
    professional: {
      name: 'Profissional',
      color: 'bg-blue-500',
      icon: '🥈'
    },
    enterprise: {
      name: 'Empresarial',
      color: 'bg-purple-500',
      icon: '🥇'
    }
  }

  const defaultFeatures = {
    basic: {
      templates: false,
      api_integration: false,
      analytics: false,
      webhooks: false,
      priority_support: false
    },
    professional: {
      templates: true,
      api_integration: true,
      analytics: true,
      webhooks: false,
      priority_support: false
    },
    enterprise: {
      templates: true,
      api_integration: true,
      analytics: true,
      webhooks: true,
      priority_support: true
    }
  }

  const loadPlans = async (page = 1) => {
    try {
      setLoading(true)
      const response = await subscriptionPlanService.getPlans(page, pagination.limit)
      setPlans(response.plans)
      setPagination(response.pagination)
      setError(null)
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao carregar planos')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadPlans()
  }, [])

  const handleCreatePlan = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      await subscriptionPlanService.createPlan(formData)
      toast.success('Plano criado com sucesso!')
      setShowCreateModal(false)
      setFormData({
        plan_type: 'basic',
        name: '',
        description: '',
        max_bots: 1,
        max_attendants: 3,
        features: {},
        price: 0,
        is_active: true
      })
      loadPlans(pagination.page)
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Erro ao criar plano'
      toast.error(errorMessage)
      setError(errorMessage)
    }
  }

  const handleUpdatePlan = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedPlan) return

    try {
      await subscriptionPlanService.updatePlan(selectedPlan.id, formData)
      toast.success('Plano atualizado com sucesso!')
      setShowEditModal(false)
      setSelectedPlan(null)
      loadPlans(pagination.page)
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Erro ao atualizar plano'
      toast.error(errorMessage)
      setError(errorMessage)
    }
  }

  const handleDeletePlan = async (id: number) => {
    if (!confirm('Tem certeza que deseja deletar este plano?')) return

    try {
      await subscriptionPlanService.deletePlan(id)
      toast.success('Plano deletado com sucesso!')
      loadPlans(pagination.page)
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Erro ao deletar plano'
      toast.error(errorMessage)
      setError(errorMessage)
    }
  }

  const handlePlanTypeChange = (planType: string) => {
    setFormData({
      ...formData,
      plan_type: planType as any,
      features: defaultFeatures[planType as keyof typeof defaultFeatures] || {}
    })
  }

  const handleFeatureChange = (feature: string, value: boolean) => {
    setFormData({
      ...formData,
      features: {
        ...formData.features,
        [feature]: value
      }
    })
  }

  const getPlanColor = (planType: string) => {
    return planTypeConfig[planType as keyof typeof planTypeConfig]?.color || 'bg-gray-500'
  }

  const getPlanIcon = (planType: string) => {
    return planTypeConfig[planType as keyof typeof planTypeConfig]?.icon || '📋'
  }

  const filteredPlans = plans.filter(plan =>
    plan.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    plan.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    plan.plan_type.toLowerCase().includes(searchTerm.toLowerCase())
  )

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
        <span className="ml-2 text-gray-600">Carregando planos...</span>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="p-2 hover:bg-gray-200 rounded-lg"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Gerenciamento de Planos
              </h1>
              <p className="text-gray-600">Configure os planos de assinatura e seus limites</p>
            </div>
          </div>
          <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="h-4 w-4 mr-2" />
              <span>Novo Plano</span>
            </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="mb-6 bg-white rounded-lg shadow-sm p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Buscar planos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          <button
            onClick={() => loadPlans(pagination.page)}
            className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
            <span className="text-red-700">{error}</span>
          </div>
        </div>
      )}

      {/* Lista de Planos */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredPlans.map((plan) => (
          <div key={plan.id} className="bg-white rounded-lg shadow-sm border hover:shadow-md transition-shadow duration-200">
            <div className="p-6">
              {/* Header do Plano */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <span className="text-2xl mr-2">{getPlanIcon(plan.plan_type)}</span>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{plan.name}</h3>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium text-white ${getPlanColor(plan.plan_type)}`}>
                      {planTypeConfig[plan.plan_type]?.name}
                    </span>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => {
                      setSelectedPlan(plan)
                      setFormData({
                        plan_type: plan.plan_type,
                        name: plan.name,
                        description: plan.description || '',
                        max_bots: plan.max_bots,
                        max_attendants: plan.max_attendants,
                        features: typeof plan.features === 'string' ? JSON.parse(plan.features) : (plan.features || {}),
                        price: plan.price || 0,
                        is_active: plan.is_active
                      })
                      setShowEditModal(true)
                    }}
                    className="text-blue-600 hover:text-blue-900"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDeletePlan(plan.id)}
                    className="text-red-600 hover:text-red-900"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* Descrição */}
              {plan.description && (
                <p className="text-gray-600 text-sm mb-4">{plan.description}</p>
              )}

              {/* Preço */}
              {plan.price && (
                <div className="flex items-center mb-4">
                  <DollarSign className="h-4 w-4 text-green-500 mr-1" />
                  <span className="text-2xl font-bold text-green-600">R$ {formatPrice(plan.price)}</span>
                  <span className="text-gray-500 text-sm ml-1">/mês</span>
                </div>
              )}

              {/* Limites */}
              <div className="space-y-2 mb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Bot className="h-4 w-4 text-gray-400 mr-2" />
                    <span className="text-sm text-gray-600">Bots</span>
                  </div>
                  <span className="font-medium">{plan.max_bots}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <UserCheck className="h-4 w-4 text-gray-400 mr-2" />
                    <span className="text-sm text-gray-600">Atendentes</span>
                  </div>
                  <span className="font-medium">{plan.max_attendants}</span>
                </div>
              </div>

              {/* Features */}
              {plan.features && (() => {
                try {
                  const featuresObj = typeof plan.features === 'string' 
                    ? JSON.parse(plan.features) 
                    : plan.features;
                  
                  if (typeof featuresObj === 'object' && featuresObj !== null && Object.keys(featuresObj).length > 0) {
                    return (
                      <div className="space-y-1">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Recursos:</h4>
                        {Object.entries(featuresObj).map(([feature, enabled]) => (
                          <div key={feature} className="flex items-center">
                            {enabled ? (
                              <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                            ) : (
                              <XCircle className="h-4 w-4 text-gray-300 mr-2" />
                            )}
                            <span className={`text-sm ${enabled ? 'text-gray-900' : 'text-gray-400'}`}>
                              {feature.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </span>
                          </div>
                        ))}
                      </div>
                    );
                  }
                  return null;
                } catch (error) {
                  logger.error('Erro ao processar features:', error);
                  return null;
                }
              })()}

              {/* Status */}
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Status:</span>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    plan.is_active 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {plan.is_active ? 'Ativo' : 'Inativo'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Modal de Criar Plano */}
      {showCreateModal && (
        <PlanModal
          title="Criar Novo Plano"
          formData={formData}
          setFormData={setFormData}
          onSubmit={handleCreatePlan}
          onClose={() => setShowCreateModal(false)}
          onPlanTypeChange={handlePlanTypeChange}
          onFeatureChange={handleFeatureChange}
        />
      )}

      {/* Modal de Editar Plano */}
      {showEditModal && selectedPlan && (
        <PlanModal
          title="Editar Plano"
          formData={formData}
          setFormData={setFormData}
          onSubmit={handleUpdatePlan}
          onClose={() => {
            setShowEditModal(false)
            setSelectedPlan(null)
          }}
          onPlanTypeChange={handlePlanTypeChange}
          onFeatureChange={handleFeatureChange}
        />
      )}
      </div>
    </div>
  )
}

// Componente Modal de Plano
interface PlanModalProps {
  title: string
  formData: CreatePlanRequest
  setFormData: (data: CreatePlanRequest) => void
  onSubmit: (e: React.FormEvent) => void
  onClose: () => void
  onPlanTypeChange: (planType: string) => void
  onFeatureChange: (feature: string, value: boolean) => void
}

const PlanModal: React.FC<PlanModalProps> = ({ 
  title, 
  formData, 
  setFormData, 
  onSubmit, 
  onClose, 
  onPlanTypeChange,
  onFeatureChange 
}) => {
  const features = [
    { key: 'templates', label: 'Templates' },
    { key: 'api_integration', label: 'Integração API' },
    { key: 'analytics', label: 'Analytics' },
    { key: 'webhooks', label: 'Webhooks' },
    { key: 'priority_support', label: 'Suporte Prioritário' }
  ]

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-6">{title}</h3>
          <form onSubmit={onSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Tipo de Plano</label>
                <select
                  value={formData.plan_type}
                  onChange={(e) => onPlanTypeChange(e.target.value)}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="basic">Básico</option>
                  <option value="professional">Profissional</option>
                  <option value="enterprise">Empresarial</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Nome</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Descrição</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Máximo de Bots</label>
                <input
                  type="number"
                  min="1"
                  required
                  value={formData.max_bots}
                  onChange={(e) => setFormData({ ...formData, max_bots: parseInt(e.target.value) })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Máximo de Atendentes</label>
                <input
                  type="number"
                  min="1"
                  required
                  value={formData.max_attendants}
                  onChange={(e) => setFormData({ ...formData, max_attendants: parseInt(e.target.value) })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Preço (R$)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Recursos</label>
              <div className="grid grid-cols-2 gap-3">
                {features.map((feature) => (
                  <label key={feature.key} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.features?.[feature.key] || false}
                      onChange={(e) => onFeatureChange(feature.key, e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">{feature.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Plano ativo</span>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="btn btn-md btn-outline"
              >
                Cancelar
              </button>
              <button type="submit" className="btn btn-md btn-primary">
                Salvar Plano
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default SubscriptionPlans
